import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';

interface LeadNotesPopoverProps {
  open: boolean;
  initialNotes: string;
  onClose: () => void;
  onSave: (notes: string) => Promise<void>;
}

const LeadNotesPopover: React.FC<LeadNotesPopoverProps> = ({ open, initialNotes, onClose, onSave }) => {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNotes(initialNotes || '');
      setError(null);
      setSaving(false);
    }
  }, [open, initialNotes]);

  const handleSave = async () => {
    if (notes.length > 2000) {
      setError('Notes are too long (max 2000 chars)');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(notes);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save notes');
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={saving ? () => {} : onClose} maxWidth="lg" ariaLabel="Edit notes">
      <div className="relative px-6 pt-6 pb-2">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit notes</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Free-text follow-up notes. Visible to all admins.
        </p>
      </div>

      <div className="px-6 pt-4 pb-6 space-y-4">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={8}
          maxLength={2000}
          disabled={saving}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zebu-500 focus:border-transparent"
          placeholder="What was discussed? Any next steps?"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
            {notes.length} / 2000
          </span>
          {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} isLoading={saving}>Save notes</Button>
        </div>
      </div>
    </Modal>
  );
};

export default LeadNotesPopover;
